import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DialogModule } from 'primeng/dialog';
import { FileBadge, getFileBadge, getFileNameFromUrl } from '../file-utils';

interface HistoryEntry {
  id: number;
  title: string;
  badgeClass: string;
  iconClass: string;
  remarks?: string | null;
  internalNotes?: string | null;
  attachments: { name: string; url: string; badge: FileBadge }[];
  time: string;
  raw: any;
}

interface HistoryGroup {
  label: string;
  entries: HistoryEntry[];
}

@Component({
  selector: 'app-application-history-dialog',
  standalone: true,
  imports: [CommonModule, DialogModule],
  templateUrl: './application-history-dialog.component.html',
  styleUrl: './application-history-dialog.component.scss'
})
export class ApplicationHistoryDialogComponent {
  /** Emits the application + milestone to edit when the pencil icon is clicked. */
  @Output() edit = new EventEmitter<{ application: any; milestone: any }>();

  /** When false, the per-entry edit (pencil) button is hidden — e.g. read-only student view. */
  @Input() canEdit = true;

  visible = false;
  appName = '';
  groups: HistoryGroup[] = [];

  private app: any = null;

  open(app: any): void {
    this.app = app;
    this.appName = app?.instituteName ?? '';

    const milestones = [...(app?.milestones ?? [])]
      .sort((a, b) => this.milestoneTime(b) - this.milestoneTime(a));

    const groups: HistoryGroup[] = [];
    const indexByLabel = new Map<string, number>();

    for (const m of milestones) {
      const label = this.dateGroupLabel(this.milestoneDate(m));
      let idx = indexByLabel.get(label);
      if (idx === undefined) {
        idx = groups.length;
        groups.push({ label, entries: [] });
        indexByLabel.set(label, idx);
      }
      groups[idx].entries.push(this.toEntry(m));
    }

    this.groups = groups;
    this.visible = true;
  }

  close(): void {
    this.visible = false;
  }

  onEdit(entry: HistoryEntry): void {
    if (!this.app) return;
    this.visible = false;
    this.edit.emit({ application: this.app, milestone: entry.raw });
  }

  exportHistory(): void {
    const lines: string[] = [`Application History — ${this.appName}`, ''];
    for (const group of this.groups) {
      lines.push(group.label);
      for (const e of group.entries) {
        lines.push(`  [${e.time || '—'}] ${e.title}`);
        if (e.remarks) lines.push(`      Remark: ${e.remarks}`);
        if (e.internalNotes) lines.push(`      Internal Notes: ${e.internalNotes}`);
        for (const a of e.attachments) lines.push(`      File: ${a.name}${a.url ? ' (' + a.url + ')' : ''}`);
      }
      lines.push('');
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `application-history-${(this.appName || 'log').replace(/\s+/g, '-').toLowerCase()}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  }

  // ---- helpers ----

  private toEntry(m: any): HistoryEntry {
    const attachments = this.attachmentUrls(m).map((url, i) => ({
      name: getFileNameFromUrl(url) || `Document ${i + 1}`,
      url,
      badge: getFileBadge(url)
    }));
    const date = this.milestoneDate(m);
    const title = m?.applicationStatusDescription || 'Status Update';
    return {
      id: m?.id,
      title,
      badgeClass: this.badgeClassFromLabel(title),
      iconClass: attachments.length ? 'pi pi-file' : 'pi pi-flag',
      remarks: m?.remarks ?? null,
      internalNotes: m?.internalTeamNotes ?? null,
      attachments,
      time: date ? this.formatTime(date) : '',
      raw: m
    };
  }

  private attachmentUrls(m: any): string[] {
    return (m?.attachment ?? [])
      .map((a: any) => (typeof a === 'string' ? a : a?.attachmentUrl))
      .filter((u: any): u is string => typeof u === 'string' && u.length > 0);
  }

  private milestoneDate(m: any): Date | null {
    return this.parseDate(m?.createdOn) ?? this.parseDate(m?.attachment?.[0]?.createdDate);
  }

  private milestoneTime(m: any): number {
    const d = this.milestoneDate(m);
    return d ? d.getTime() : 0;
  }

  private parseDate(value: any): Date | null {
    if (!value) return null;
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }

  private dateGroupLabel(d: Date | null): string {
    if (!d) return 'Earlier';
    const fmt = d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).toUpperCase();
    const isToday = d.toDateString() === new Date().toDateString();
    return isToday ? `TODAY · ${fmt}` : fmt;
  }

  private formatTime(d: Date): string {
    return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  }

  private badgeClassFromLabel(label: string): string {
    const l = (label || '').toLowerCase();
    if (l.includes('reject') || l.includes('denied')) return 'badge-red';
    if (l.includes('approve') || l.includes('completed') || l.includes('enrolled') || l.includes('offer') || l.includes('paid')) return 'badge-green';
    if (l.includes('progress') || l.includes('assess') || l.includes('pending') || l.includes('review')) return 'badge-orange';
    if (l.includes('sent') || l.includes('submitted') || l.includes('applied') || l.includes('uploaded')) return 'badge-blue';
    return 'badge-gray';
  }
}
